import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CallWrapUp } from "./CallWrapUp";
import { DTMF_KEYS } from "./dtmf";
import { useCall } from "./queries";
import { useSoftphone } from "./SoftphoneProvider";
import type { CallPhase } from "./state";
import { BRIDGE, formatDuration, isTerminal } from "./state";
import { canSendTones, useSoftphoneStore } from "./store";

/**
 * Six phases, two grounds. Modernist is mono and the accent is the only colour
 * in it, so a live call is red and everything else is the ink — which is also
 * the honest encoding: what the bar is *for* is telling a seller a call is
 * running while they click around the CRM (.docs/04 §6).
 */
const TONES: Record<CallPhase, string> = {
  idle: "bg-ink",
  starting: "bg-ink",
  ringing: "bg-ink",
  in_progress: "bg-brand-700",
  ended: "bg-ink",
  failed: "bg-brand-900",
};

/**
 * Counts up from the client's first ring, once a second.
 *
 * From the ring and not from the answer: a phone that rings out took the agent
 * that long too, and it is the figure the call's entry on the timeline carries.
 */
function Elapsed({
  since,
  final,
  running,
}: {
  since: number | null;
  final: number | null;
  /** The call is still up. A finished one holds whatever it last showed. */
  running: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (since === null || final !== null || !running) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [since, final, running]);

  // The server's seconds once the call is over — it is what went on the
  // record, and our own clock drifts from it by however long the last
  // callback took to arrive.
  if (final !== null) return <span>{formatDuration(final)}</span>;
  if (since === null) return null;
  return <span>{formatDuration((now - since) / 1000)}</span>;
}

/**
 * Why a call did not connect.
 *
 * The socket payload carries only ref, status and duration, so Twilio's error
 * code is not in the store and cannot be. It is on the `Call` row, and the
 * same event already invalidated that query — so by the time this renders the
 * cache is being refilled with it. Fetched only on a failure: a request per
 * call, for a line that is blank on every successful one, is not worth it.
 */
function FailureDetail({ callRef }: { callRef: string }) {
  const { data } = useCall(callRef);
  if (!data?.error_code) return null;
  return <span className="text-ground/70"> · {data.error_code}</span>;
}

/**
 * A telephone's twelve keys, for the switchboard that asks for an extension.
 *
 * In the bar because the bar is wherever the call is — the company's detail,
 * the agenda's event, *Llamar* — and the menu is answered from where the agent
 * happens to be. The computer's own keys send the same tones without opening
 * this at all (`SoftphoneProvider`); the pad is for a mouse, and for a phone.
 */
function TonePad({ onKey }: { onKey: (key: string) => void }) {
  const { t } = useTranslation();
  return (
    // A row of its own, under the buttons that opened it: left to wrap, it
    // lands beside them wherever the bar happens to be wide enough.
    <div className="flex w-full justify-end">
      <div
        role="group"
        aria-label={t("call.tonePad")}
        className="grid w-full max-w-[228px] grid-cols-3 gap-1.5"
      >
        {DTMF_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onKey(key)}
            className="h-11 border-2 border-ground/40 font-display text-lg font-extrabold text-ground hover:bg-ground/15"
          >
            {key}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The call, wherever the agent has navigated to.
 *
 * One bar for both modes. In bridge mode there is no browser leg to mute and
 * no SDK behind any of it, but the agent is on the same call and should not
 * have to learn two interfaces to end it (.docs/04 §6).
 */
export function CallBar() {
  const { t } = useTranslation();
  const call = useSoftphoneStore((state) => state.call);
  const error = useSoftphoneStore((state) => state.error);
  const { hangup, toggleMute, sendDigits } = useSoftphone();
  const [padOpen, setPadOpen] = useState(false);
  const callRef = call?.ref;
  // A pad left open belongs to the call it was opened on, not to the next one.
  useEffect(() => setPadOpen(false), [callRef]);

  if (!call && !error) return null;

  if (!call) {
    return (
      <div
        role="alert"
        className="on-dark flex shrink-0 items-center gap-4 bg-brand-900 px-4 py-3 text-sm text-ground"
      >
        <span className="flex-1">{error}</span>
        <button
          type="button"
          onClick={() => useSoftphoneStore.getState().setError(null)}
          className="btn border-2 border-ground/40 px-3 py-1.5 text-ground hover:bg-ground/15"
        >
          {t("call.dismiss")}
        </button>
      </div>
    );
  }

  const finished = isTerminal(call.phase);
  const tones = canSendTones(call);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`on-dark flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm text-ground ${TONES[call.phase]}`}
    >
      {/* `basis-40`: on a phone the controls no longer fit beside the name, and
          without a width of its own to claim the name is what gave way — down
          to its first letter. With one, the controls drop to the next row. */}
      <div className="min-w-0 flex-1 basis-40">
        <p className="truncate font-display font-extrabold">
          {call.label || call.dialedNumber}
          {call.phase === "failed" ? <FailureDetail callRef={call.ref} /> : null}
        </p>
        <p className="truncate text-ground/70">
          {t(`call.phase.${call.phase}`)}
          {call.label ? ` · ${call.dialedNumber}` : null}
          {/* What was pressed, pad open or not: a key on the computer's
              keyboard makes a tone and nothing else to look at. */}
          {call.digits ? ` · ${t("call.tones", { digits: call.digits })}` : null}
        </p>
      </div>

      {/* One group, so the clock and the buttons wrap together or not at all —
          and to the right when they do, over the pad they open. */}
      <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="tabular-nums" aria-label={t("call.elapsed")}>
          <Elapsed since={call.ringingAt} final={call.duration} running={!finished} />
        </span>

        {!finished && call.mode !== BRIDGE ? (
          <button
            type="button"
            onClick={toggleMute}
            aria-pressed={call.muted}
            className="btn border-2 border-ground/40 px-3 py-1.5 text-ground hover:bg-ground/15"
          >
            {t(call.muted ? "call.unmute" : "call.mute")}
          </button>
        ) : null}

        {tones ? (
          <button
            type="button"
            onClick={() => setPadOpen((open) => !open)}
            aria-expanded={padOpen}
            className="btn border-2 border-ground/40 px-3 py-1.5 text-ground hover:bg-ground/15"
          >
            {t("call.keypad")}
          </button>
        ) : null}

        {finished ? null : (
          <button
            type="button"
            onClick={hangup}
            // Inverted: on a red or ink bar the loudest thing available is the
            // ground itself, and hanging up is the loudest action there is.
            className="btn bg-ground px-3 py-1.5 font-display font-extrabold text-ink hover:bg-mute-200"
          >
            {t("call.hangup")}
          </button>
        )}
      </div>

      {error ? <span className="w-full text-ground/90">{error}</span> : null}

      {tones && padOpen ? <TonePad onKey={sendDigits} /> : null}

      {/* The write-up replaces the bare "Cerrar": a call that is never written
          up leaves the client's timeline with a gap, and it is the only moment
          the agent can record a consent they just asked for (.docs/06 §2.1).
          Dismissing without saving is one click away, inside the form — but
          only on a call that never connected: one the client answered is put
          away by saving its summary. */}
      {finished ? <CallWrapUp callRef={call.ref} /> : null}
    </div>
  );
}
